import React, { useRef, useEffect } from "react";
import { useVSU } from "../store";
import { pick, fmtDate, posterDataUrl } from "../helpers";
import EmptyState from "./EmptyState";

// Область воспроизведения (5.5, RQ.20.03/RQ.20.06).
// Файл {id}/file отдаётся публично (permitAll) с поддержкой Range → нативный <video src>,
// потоковая подгрузка и перемотка работают. Скачивать целиком (blob) не нужно.
export default function VideoPlayer({ containerRef }) {
  const { t, lang, selected, selectedId, setPlaying } = useVSU();
  const videoRef = useRef(null);

  const available = !!selected?.video?.available;
  const url = selected?.video?.url || null;

  // Переключение записи (5.5): pause() → removeAttribute("src") → load()
  // (отмена загрузки, сброс буфера) → новый src, позиция в начало.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    el.pause();
    el.removeAttribute("src");
    el.load();

    if (available && url) {
      el.setAttribute("src", url);
      el.load();
      try {
        el.currentTime = 0;
      } catch {
        /* до метаданных currentTime может быть недоступен */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, url, available]);

  // Пустая выборка — «ничего не выбрано».
  if (selectedId === null || !selected) {
    return (
      <div className="vsu-player-box">
        <div className="vsu-player" ref={containerRef}>
          <EmptyState icon="▤" title={t("emptyT")} desc={t("emptyD")} />
        </div>
      </div>
    );
  }

  // АС1 UC.12.01 — видеозапись недоступна (video.available === false).
  if (!available) {
    return (
      <div className="vsu-player-box">
        <div className="vsu-player" ref={containerRef}>
          <div className="vsu-noplay">
            <div className="vsu-noplay-ic">⚠</div>
            <b>{t("noVideoT")}</b>
            <div>{t("noVideoD")}</div>
          </div>
        </div>
        <Meta selected={selected} lang={lang} t={t} />
      </div>
    );
  }

  // Доступная запись.
  return (
    <div className="vsu-player-box">
      <div className="vsu-player" ref={containerRef}>
        <video
          ref={videoRef}
          className="vsu-video"
          controls
          preload="metadata"
          poster={posterDataUrl(selected, lang)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />
      </div>
      <Meta selected={selected} lang={lang} t={t} />
    </div>
  );
}

// Метаданные под плеером. Отсутствующие даты — «—». Имя видеофайла справочно (не идентификатор).
function Meta({ selected, lang, t }) {
  if (!selected) return null;
  const v = selected.video || {};
  const f = v.available ? v.file_name : null;
  return (
    <div className="vsu-pmeta">
      <div>
        {t("colSrc")}: <b>{pick(selected.source_name, lang)}</b>
      </div>
      <div>
        {t("colLoc")}: <b>{pick(selected.location, lang)}</b>
      </div>
      <div>
        {t("colAddr")}: <b>{pick(selected.address, lang)}</b>
      </div>
      <div>
        {t("colFile")}: <b>{f || "—"}</b>
      </div>
      <div>
        {t("colPlan")}: <b>{fmtDate(selected.plan_date) || "—"}</b>
      </div>
      <div>
        {t("colFact")}: <b>{fmtDate(selected.fact_date) || "—"}</b>
      </div>
    </div>
  );
}
